import PropTypes from "prop-types";
import { useCallback, useMemo, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DataTable from "examples/Tables/DataTable";

import PanelCard from "layouts/dutyEstimator/components/PanelCard";
import levyOverrideTableData from "layouts/dutyEstimator/data/levyOverrideTableData";
import { LEVY_KEY_OPTIONS, INPUT_SX, WHITE_BUTTON_SX, GREEN_BUTTON_SX } from "layouts/dutyEstimator/constants";

const EMPTY_FORM = { categoryId: "", levyKey: "", isExempt: false, overrideRate: "", notes: "", isActive: true };

// 2026-08-04: lets any category be exempted from, or charge a different rate for, any of the
// statutory levies (Import VAT, NHIL, GETFund, ECOWAS, AU, EXIM, Special Import Levy, Withholding
// Tax) — generalizing what the "Charges Network Fee" toggle already does for the Network Charge
// specifically. A category with no row for a given levy simply uses the global Settings rate.
// Deliberately seeded with no rows out of the box: only add one once a specific exemption has
// actually been confirmed with the client, not a general policy statement.
function LevyOverridesPanel({ overrides, categoryOptions, onSave, onDelete, onValidationError }) {
  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);

  const handleAdd = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setFormOpen(true);
  };

  const handleEdit = useCallback((override) => {
    setEditingId(override?.id);
    setForm({
      categoryId: override?.categoryId ?? "",
      levyKey: override?.levyKey ?? "",
      isExempt: Boolean(override?.isExempt),
      overrideRate: override?.overrideRate ?? "",
      notes: override?.notes ?? "",
      isActive: override?.isActive !== false,
    });
    setFormOpen(true);
  }, []);

  const handleSubmit = async () => {
    if (!form.categoryId || !form.levyKey) {
      onValidationError("Please choose a category and a levy.");
      return;
    }
    if (!form.isExempt && form.overrideRate === "") {
      onValidationError(
        "Either mark this category exempt, or enter the rate it should be charged instead — otherwise this override does nothing."
      );
      return;
    }
    if (!form.notes.trim()) {
      onValidationError("Please note why this override exists (e.g. what the client confirmed, and when).");
      return;
    }

    const saved = await onSave(editingId, {
      categoryId: Number(form.categoryId),
      levyKey: form.levyKey,
      isExempt: form.isExempt,
      overrideRate: form.isExempt || form.overrideRate === "" ? null : Number(form.overrideRate),
      notes: form.notes.trim(),
      isActive: form.isActive,
    });
    if (saved) setFormOpen(false);
  };

  const { columns, rows } = useMemo(
    () => levyOverrideTableData({ overrideList: overrides, onEdit: handleEdit, onDelete }),
    [overrides, handleEdit, onDelete]
  );

  return (
    <>
      <PanelCard
        title="Category Levy Overrides"
        caption="Exempt a specific category from a levy, or charge it a different rate, without changing the global rate every other category still uses. Leave a levy alone here and it simply follows Settings."
        action={
          <Button variant="contained" size="small" onClick={handleAdd} sx={WHITE_BUTTON_SX}>
            Add Override
          </Button>
        }
      >
        <MDBox pt={3}>
          <DataTable
            table={{ columns, rows }}
            isSorted={false}
            entriesPerPage={{ defaultValue: 10, entries: [10, 15, 25] }}
            showTotalEntries={false}
            noEndBorder
            canSearch={false}
          />
        </MDBox>
      </PanelCard>

      <Dialog open={formOpen} onClose={() => setFormOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle>{editingId ? "Edit Levy Override" : "Add Levy Override"}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} pt={1}>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                autoFocus
                fullWidth
                label="Category"
                value={form.categoryId}
                onChange={(e) => setForm({ ...form, categoryId: e.target.value })}
                sx={INPUT_SX}
                SelectProps={{ IconComponent: ArrowDropDownIcon }}
                disabled={Boolean(editingId)}
                helperText={editingId ? "Category cannot be changed once saved." : undefined}
              >
                {categoryOptions.length === 0 ? (
                  <MenuItem disabled value="">
                    No categories available
                  </MenuItem>
                ) : (
                  categoryOptions.map((cat) => (
                    <MenuItem key={cat.id} value={cat.id}>
                      {cat.label}
                    </MenuItem>
                  ))
                )}
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label="Levy"
                value={form.levyKey}
                onChange={(e) => setForm({ ...form, levyKey: e.target.value })}
                sx={INPUT_SX}
                SelectProps={{ IconComponent: ArrowDropDownIcon }}
                disabled={Boolean(editingId)}
                helperText={editingId ? "Levy cannot be changed once saved." : "One override per category + levy."}
              >
                {LEVY_KEY_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6}>
              <MDBox display="flex" alignItems="center" height="100%">
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.isExempt}
                      onChange={(e) => setForm({ ...form, isExempt: e.target.checked, overrideRate: "" })}
                    />
                  }
                  label={
                    <MDTypography variant="button" fontWeight="regular">
                      Exempt (charge 0%)
                    </MDTypography>
                  }
                />
              </MDBox>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Override Rate (%) instead"
                value={form.overrideRate}
                onChange={(e) => setForm({ ...form, overrideRate: e.target.value })}
                disabled={form.isExempt}
                helperText={form.isExempt ? "Not used while Exempt is on" : "Charged instead of the global rate"}
                sx={INPUT_SX}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <MDBox display="flex" alignItems="center" height="100%">
                <FormControlLabel
                  control={
                    <Switch checked={form.isActive} onChange={(e) => setForm({ ...form, isActive: e.target.checked })} />
                  }
                  label={
                    <MDTypography variant="button" fontWeight="regular">
                      Active
                    </MDTypography>
                  }
                />
              </MDBox>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={2}
                label="Notes — why does this override exist?"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                helperText="e.g. what the client confirmed, and when — required so this is traceable later."
                sx={INPUT_SX}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFormOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit} sx={GREEN_BUTTON_SX}>
            {editingId ? "Save Changes" : "Add Override"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

LevyOverridesPanel.propTypes = {
  overrides: PropTypes.array.isRequired,
  categoryOptions: PropTypes.array.isRequired,
  onSave: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onValidationError: PropTypes.func.isRequired,
};

export default LevyOverridesPanel;
